package ai.jessie.web.customers

import ai.jessie.shared.customers.CustomerAddressInput
import ai.jessie.shared.customers.CustomerContactInput
import ai.jessie.shared.customers.CustomerResponse
import ai.jessie.shared.customers.SaveCustomerRequest

// Editable text as entered. Child IDs are kept so that saved children keep their identity.
data class CustomerDraft(
    val type: String = "",
    val displayName: String = "",
    val email: String = "",
    val phone: String = "",
    val status: String = "ACTIVE",
    val addresses: List<AddressDraft> = emptyList(),
    val contacts: List<ContactDraft> = emptyList(),
)

data class AddressDraft(
    val id: String? = null,
    val type: String = "",
    val line1: String = "",
    val line2: String = "",
    val postalCode: String = "",
    val city: String = "",
    val countryCode: String = "",
)

// BR-066: a new contact is undesignated until the member explicitly selects it.
data class ContactDraft(
    val id: String? = null,
    val name: String = "",
    val email: String = "",
    val phone: String = "",
    val role: String = "",
    val billingContact: Boolean = false,
)

internal fun CustomerResponse.draft(status: String = this.status) =
    CustomerDraft(
        type = type,
        displayName = displayName,
        email = email.orEmpty(),
        phone = phone.orEmpty(),
        status = status,
        addresses =
            addresses.map {
                AddressDraft(
                    it.id,
                    it.type,
                    it.line1,
                    it.line2.orEmpty(),
                    it.postalCode,
                    it.city,
                    it.countryCode,
                )
            },
        contacts =
            contacts.map { contact ->
                with(contact.details) {
                    ContactDraft(
                        id,
                        name,
                        email.orEmpty(),
                        phone.orEmpty(),
                        role.orEmpty(),
                        billingContact,
                    )
                }
            },
    )

// The server validates; the client only trims and sends an empty optional value as absent.
internal fun CustomerDraft.request(expectedUpdatedAt: String?) =
    SaveCustomerRequest(
        type = type,
        displayName = displayName.trim(),
        email = email.optional(),
        phone = phone.optional(),
        status = status,
        addresses =
            addresses.map {
                CustomerAddressInput(
                    it.id,
                    it.type,
                    it.line1.trim(),
                    it.line2.optional(),
                    it.postalCode.trim(),
                    it.city.trim(),
                    it.countryCode.trim().uppercase(),
                )
            },
        contacts =
            contacts.map {
                CustomerContactInput(
                    it.id,
                    it.name.trim(),
                    it.email.optional(),
                    it.phone.optional(),
                    it.role.optional(),
                    it.billingContact,
                )
            },
        expectedUpdatedAt = expectedUpdatedAt,
    )

private fun String.optional() = trim().ifEmpty { null }
